import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Card, Switch, FormControlLabel } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import SkLoading from "components/SkLoading";
import AstrieskIcon from "components/AstrieskIcon";
import { useMaterialUIController } from "context";

const Form = ({ setIsOpen, onSuccess, isOpenUpdate, singleData }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [loading, setLoading] = useState(false);
  const [isData, setIsData] = useState({
    heading: "",
    description: "",
    diasble: false,
  });

  // Pre-fill form when editing
  useEffect(() => {
    if (isOpenUpdate && singleData) {
      setIsData({
        heading: singleData.heading || "",
        description: singleData.description || "",
        diasble: singleData.diasble || false,
      });
    } else {
      setIsData({ heading: "", description: "", diasble: false });
    }
  }, [isOpenUpdate, singleData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setIsData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isData.heading.trim() || !isData.description.trim()) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      let res;
      if (isOpenUpdate && singleData?._id) {
        // UPDATE
        res = await axios.put(
          `https://api.homelyx.framekarts.com/api/help-support/update/${singleData._id}`,
          {
            heading: isData.heading,
            description: isData.description,
            diasble: isData.diasble,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      } else {
        // CREATE
        res = await axios.post(
          "https://api.homelyx.framekarts.com/api/help-support/create",
          {
            heading: isData.heading,
            description: isData.description,
            diasble: isData.diasble,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      }

      if (res.data?.success) {
        setIsData({ heading: "", description: "", diasble: false });
        setIsOpen(false);
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      
    } finally {
      setLoading(false);
    }
  };

  return loading ? (
    <SkLoading />
  ) : (
    <>
      <MDBox
        display="flex"
        alignItems="center"
        lineHeight={1}
        sx={{
          justifyContent: "center",
          textAlign: "center",
          flexDirection: "column",
          gap: 2,
          width: "100%",
        }}
      >
        {/* Title Card */}
        <Card
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 10px",
          }}
        >
          <MDTypography variant="h6" py={0.9}>
            {isOpenUpdate ? "Update Help & Support" : "Create Help & Support"}
          </MDTypography>
        </Card>

        {/* Form Body */}
        <MDBox
          display="flex"
          alignItems="center"
          lineHeight={1}
          sx={({ palette: { dark, white } }) => ({
            border: 0.5,
            borderColor: darkMode ? white.main : dark.main,
            borderRadius: 3,
            p: 2,
            justifyContent: "center",
            textAlign: "center",
            flexDirection: "column",
            gap: 2,
            width: "100%",
          })}
          component="form"
          role="form"
          onSubmit={handleSubmit}
        >
          {/* Heading */}
          <MDBox
            lineHeight={1}
            gap={1}
            width="90%"
            sx={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}
          >
            <MDTypography variant="button" fontWeight="medium">
              Heading <AstrieskIcon />
            </MDTypography>
            <MDInput
              required
              type="text"
              placeholder="e.g. PAYMENT & REFUND ISSUE"
              fullWidth
              name="heading"
              value={isData.heading}
              onChange={handleChange}
            />
          </MDBox>

          {/* Description */}
          <MDBox
            lineHeight={1}
            gap={1}
            width="90%"
            sx={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}
          >
            <MDTypography variant="button" fontWeight="medium">
              Description <AstrieskIcon />
            </MDTypography>
            <MDInput
              required
              type="text"
              placeholder="e.g. Issue related to payment and refund"
              fullWidth
              multiline
              rows={2}
              name="description"
              value={isData.description}
              onChange={handleChange}
            />
          </MDBox>

          {/* Status Toggle */}
          <MDBox
            lineHeight={1}
            gap={1}
            width="90%"
            sx={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}
          >
            <MDTypography variant="button" fontWeight="medium">
              Status
            </MDTypography>
            <FormControlLabel
              control={
                <Switch
                  checked={!isData.diasble}
                  onChange={(e) => setIsData((prev) => ({ ...prev, diasble: !e.target.checked }))}
                  color="info"
                />
              }
              label={
                <MDTypography variant="button" fontWeight="regular">
                  {isData.diasble ? "Disabled" : "Active"}
                </MDTypography>
              }
            />
          </MDBox>

          {/* Submit */}
          <MDBox
            sx={{
              width: "100%",
              justifyContent: "flex-end",
              textAlign: "center",
              display: "flex",
            }}
          >
            <MDButton color="info" variant="gradient" type="submit">
              {isOpenUpdate ? "Update Help & Support" : "Create Help & Support"}
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
    </>
  );
};

export default Form;

Form.propTypes = {
  setIsOpen: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  isOpenUpdate: PropTypes.bool,
  singleData: PropTypes.object,
};
